if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    class ProductPersonalization extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.productInfo = this.closest('product-info');
        this.checkbox = this.querySelector('.product-personalization__checkbox');
        this.fields = this.querySelector('.product-personalization__fields');
        this.nameInput = this.querySelector('[name="properties[Nombre]"]');
        this.numberInput = this.querySelector('[name="properties[Número]"]');
        this.patchInputs = [...this.querySelectorAll('[data-patch-value]')];
        this.currentBasePrice = Number(this.dataset.basePrice) || 0;

        if (!this.form || !this.checkbox || !this.fields || !this.nameInput || !this.numberInput) return;

        this.handleToggle = this.handleToggle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleFieldInput = this.handleFieldInput.bind(this);
        this.handlePatchChange = this.handlePatchChange.bind(this);

        this.checkbox.addEventListener('change', this.handleToggle);
        this.nameInput.addEventListener('input', this.handleFieldInput);
        this.numberInput.addEventListener('input', this.handleFieldInput);
        this.patchInputs.forEach((input) => input.addEventListener('change', this.handlePatchChange));
        this.form.addEventListener('submit', this.handleSubmit, true);
        this.productInfo?.addEventListener('change', this.handleVariantChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.sectionId) return;
            if (Number.isFinite(Number(data.variant?.price))) this.currentBasePrice = Number(data.variant.price);
            this.updatePresentation();
          });
        }

        this.restoreState();
        this.updatePresentation();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener('change', this.handleToggle);
        this.nameInput?.removeEventListener('input', this.handleFieldInput);
        this.numberInput?.removeEventListener('input', this.handleFieldInput);
        this.patchInputs?.forEach((input) => input.removeEventListener('change', this.handlePatchChange));
        this.form?.removeEventListener('submit', this.handleSubmit, true);
        this.productInfo?.removeEventListener('change', this.handleVariantChange);
        this.variantChangeUnsubscriber?.();
      }

      get stateKey() {
        return this.dataset.productFormId;
      }

      get stateStore() {
        window.grada90PersonalizationState ||= new Map();
        return window.grada90PersonalizationState;
      }

      get active() {
        return Boolean(this.checkbox?.checked);
      }

      get selectedPatchValue() {
        const customPatchControl = this.querySelector('[data-patch-value]:checked');
        if (customPatchControl) return customPatchControl.value;

        const variantSelects = this.productInfo?.querySelector('variant-selects');
        if (!variantSelects) return '';

        const patchControl = variantSelects.querySelector('[data-option-name="Parche"]:checked');
        if (patchControl) return patchControl.value;

        const patchOption = variantSelects.querySelector('select option[data-option-name="Parche"][selected]');
        return patchOption?.value || '';
      }

      get hasSelectedPatch() {
        const value = this.selectedPatchValue
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();
        return Boolean(value) && value !== 'sin parche';
      }

      restoreState() {
        const saved = this.stateStore.get(this.stateKey);
        this.checkbox.checked = Boolean(saved?.active);
        this.nameInput.value = saved?.name || '';
        this.numberInput.value = saved?.number || '';
        if (saved?.patch) {
          const savedPatch = this.patchInputs.find((input) => input.value === saved.patch);
          if (savedPatch) savedPatch.checked = true;
        }
        this.applyFieldState(this.active, false);
      }

      saveState() {
        this.stateStore.set(this.stateKey, {
          active: this.active,
          name: this.active ? this.nameInput.value : '',
          number: this.active ? this.numberInput.value : '',
          patch: this.selectedPatchValue,
        });
      }

      handleToggle() {
        this.applyFieldState(this.active, !this.active);
        this.saveState();
        this.updatePresentation();
        if (this.active) this.nameInput.focus();
      }

      handleFieldInput() {
        this.nameInput.setCustomValidity('');
        this.numberInput.setCustomValidity('');
        this.saveState();
      }

      handlePatchChange() {
        this.saveState();
        this.updatePresentation();
      }

      handleVariantChange(event) {
        if (!event.target.closest('variant-selects')) return;
        queueMicrotask(() => this.updatePresentation());
      }

      applyFieldState(active, clearValues) {
        this.fields.hidden = !active;
        [this.nameInput, this.numberInput].forEach((input) => {
          input.disabled = !active;
          input.required = false;
          input.setCustomValidity('');
          if (clearValues) input.value = '';
        });
      }

      updatePresentation() {
        const active = this.active;
        this.productInfo?.querySelectorAll('[data-patch-price]').forEach((element) => {
          element.hidden = active;
        });
        this.productInfo?.querySelectorAll('[data-patch-included]').forEach((element) => {
          element.hidden = !active;
        });

        const message = this.productInfo?.querySelector('[data-patch-included-message]');
        if (message) message.hidden = !(active && this.hasSelectedPatch);

        this.updateDisplayedPrice();
      }

      updateDisplayedPrice() {
        const fee = this.active
          ? Number(this.dataset.personalizationFeePrice)
          : this.hasSelectedPatch
            ? Number(this.dataset.patchFeePrice)
            : 0;
        const displayedPrice = this.formatMoney(this.currentBasePrice + fee);
        const priceContainer = document.getElementById(`price-${this.dataset.sectionId}`);
        if (!priceContainer) return;

        priceContainer
          .querySelectorAll('.price__regular .price-item--regular:not(.variant-item__old-price), .price__sale .price-item--sale')
          .forEach((element) => {
            element.textContent = displayedPrice;
          });

        this.productInfo?.querySelector('mobile-product-bar')?.syncFromMainForm?.();
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
        const token = format.match(/\{\{\s*(amount[^}]*)\s*\}\}/)?.[1]?.trim() || 'amount_with_comma_separator';
        const formats = {
          amount: [2, ',', '.'],
          amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','],
          amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_space_separator: [2, ' ', ','],
          amount_no_decimals_with_space_separator: [0, ' ', ','],
          amount_with_apostrophe_separator: [2, "'", '.'],
        };
        const [decimals, thousandsSeparator, decimalSeparator] = formats[token] || formats.amount_with_comma_separator;
        const value = (cents / 100).toFixed(decimals);
        let [whole, fraction] = value.split('.');
        whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        const amount = decimals ? `${whole}${decimalSeparator}${fraction}` : whole;
        return format.replace(/\{\{\s*amount[^}]*\s*\}\}/, amount);
      }

      handleSubmit(event) {
        if (!this.active) return;

        this.nameInput.value = this.nameInput.value.trim();
        this.saveState();

        if (!this.nameInput.value && !this.numberInput.value) {
          this.nameInput.setCustomValidity('Introduce al menos un nombre o un número.');
        }

        const invalidInput = [this.nameInput, this.numberInput].find((input) => !input.checkValidity());
        if (!invalidInput) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        invalidInput.reportValidity();
      }

      buildCartItems(formData) {
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        if (!variantId) return null;

        const parentProperties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '' && !['Nombre', 'Número'].includes(match[1])) {
            parentProperties[match[1]] = value;
          }
        }
        if (this.hasSelectedPatch) parentProperties.Parche = this.selectedPatchValue;

        const parentItem = { id: Number(variantId), quantity };
        if (Object.keys(parentProperties).length) parentItem.properties = parentProperties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) parentItem.selling_plan = Number(sellingPlan);

        let feeVariantId = '';
        let feeType = '';
        if (this.active) {
          feeVariantId = this.dataset.personalizationFeeVariantId;
          feeType = 'Personalización';
        } else if (this.hasSelectedPatch) {
          feeVariantId = this.dataset.patchFeeVariantId;
          feeType = 'Parche';
        }

        if (!feeVariantId) return null;

        const childProperties = { _Tipo: feeType };
        if (this.active) {
          if (this.nameInput.value) childProperties.Nombre = this.nameInput.value;
          if (this.numberInput.value) childProperties.Número = this.numberInput.value;
          if (this.hasSelectedPatch) childProperties._Parche = this.selectedPatchValue;
        } else if (this.hasSelectedPatch) {
          childProperties._Parche = this.selectedPatchValue;
        }

        return [
          parentItem,
          {
            id: Number(feeVariantId),
            quantity,
            parent_id: Number(variantId),
            properties: childProperties,
          },
        ];
      }
    }
  );
}
